import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogTrigger } from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import { cn } from "@/lib/utils";
import { RadioGroup, RadioGroupItem } from "../../@/components/ui/radio-group";

function isRecurringPattern(rruleString) {
    if (!rruleString) return false;
    const raw = String(rruleString).trim();
    if (!raw) return false;
    const body = raw.startsWith("RRULE:") ? raw.slice("RRULE:".length) : raw;
    const match = /\bCOUNT=(\d+)\b/i.exec(body);
    if (match) {
        const count = Number(match[1]);
        if (Number.isFinite(count) && count <= 1) return false;
    }
    // If there's no COUNT, assume it's a real recurring rule.
    return true;
}

export default function DeleteClassSession({ sessionId, sessionTitle, classId, recurrencePattern, onDeleted, triggerButtonProps = {} }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { getToken } = useAuth();

    const normalizedClassId = useMemo(() => {
        const raw = classId;
        if (raw === undefined || raw === null || raw === "") return null;
        const asNumber = Number(raw);
        return Number.isFinite(asNumber) ? asNumber : String(raw);
    }, [classId]);

    const [deleteScope, setDeleteScope] = useState("single");

    const allowBulkDelete = useMemo(() => {
        if (normalizedClassId === null) return false;
        // Only hide bulk options when we can confidently determine it's not recurring.
        if (recurrencePattern != null && recurrencePattern !== "") {
            return isRecurringPattern(recurrencePattern);
        }
        return true;
    }, [normalizedClassId, recurrencePattern]);

    useEffect(() => {
        if (!allowBulkDelete && deleteScope !== "single") {
            setDeleteScope("single");
        }
    }, [allowBulkDelete, deleteScope]);

    const handleDelete = async () => {
        setLoading(true);
        try {
            const token = await getToken();
            if (!token) return;
            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            };

            if (deleteScope === "upcoming" || deleteScope === "all") {
                if (normalizedClassId === null) {
                    toast.error("Missing class id — cannot delete multiple sessions.");
                    return;
                }

                const url =
                    deleteScope === "all"
                        ? api_endpoint + `/liveclass/class/${normalizedClassId}/all/sessions`
                        : api_endpoint + `/liveclass/class/${normalizedClassId}/upcoming/sessions`;

                try {
                    await axios.delete(url, reqConfig);
                    toast.success(
                        deleteScope === "all"
                            ? `All sessions for "${sessionTitle}" deleted successfully!`
                            : `Upcoming sessions for "${sessionTitle}" deleted successfully!`
                    );
                    setOpen(false);
                    if (onDeleted) onDeleted({ sessionId, classId: normalizedClassId, scope: deleteScope });
                    return;
                } catch (error) {
                    const status = error?.response?.status;
                    // If backend doesn't support bulk delete for this class, fall back to deleting only this session.
                    if (status === 404) {
                        await axios.delete(api_endpoint + `/liveclass/session/${sessionId}`, reqConfig);
                        toast.success(`Deleted "${sessionTitle}" (bulk delete not available for this class).`);
                        setOpen(false);
                        if (onDeleted) onDeleted({ sessionId, classId: normalizedClassId, scope: "single" });
                        return;
                    }
                    throw error;
                }
            }

            await axios.delete(api_endpoint + `/liveclass/session/${sessionId}`, reqConfig);
            toast.success(`Deleted "${sessionTitle}" successfully!`);
            setOpen(false);
            if (onDeleted) onDeleted({ sessionId, classId: normalizedClassId, scope: "single" });
        } catch (error) {
            console.error("Delete session error:", error);
            toast.error(`Failed to delete class session "${sessionTitle}".`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                    <Button
                        variant="outline"
                        size=""
                        className=""
                        {...triggerButtonProps}
                    >
                        Delete Session
                    </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md text-(--neutral-900)">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{allowBulkDelete ? "Delete Recurring Event" : "Delete Class Session"}</DialogTitle>
                    </DialogHeader>
                    <div className="font-body space-y-3">
                        <p>
                            Are you sure you want to delete <b>{sessionTitle}</b>? This action cannot be undone.
                        </p>

                        <div className="space-y-2">
                            <p className="text-sm text-(--neutral-800)">Apply changes to</p>

                            <RadioGroup
                                value={deleteScope}
                                onValueChange={(v) => {
                                    if (!allowBulkDelete && v !== "single") return;
                                    setDeleteScope(v);
                                }}
                                className="space-y-3"
                            >
                                <label
                                    className={cn(
                                        "w-full rounded-md border p-3 flex items-center gap-3 text-left",
                                        deleteScope === "single" ? "border-(--color-primary)" : "border-(--neutral-300)"
                                    )}
                                >
                                    <RadioGroupItem
                                        value="single"
                                        className={cn(
                                            "mt-0.5",
                                            "data-[state=checked]:border-(--color-primary) data-[state=checked]:text-(--color-primary)",
                                            "[&_[data-slot=radio-group-indicator]_svg]:!fill-(--color-primary)!"
                                        )}
                                    />
                                    <span className="font-medium">This session only</span>
                                </label>

                                <label
                                    className={cn(
                                        "w-full rounded-md border p-3 flex items-center gap-3 text-left",
                                        deleteScope === "upcoming" ? "border-(--color-primary)" : "border-(--neutral-300)",
                                        !allowBulkDelete && "opacity-60 cursor-not-allowed"
                                    )}
                                >
                                    <RadioGroupItem
                                        value="upcoming"
                                        disabled={!allowBulkDelete}
                                        className={cn(
                                            "mt-0.5",
                                            "data-[state=checked]:border-(--color-primary) data-[state=checked]:text-(--color-primary)",
                                            "[&_[data-slot=radio-group-indicator]_svg]:!fill-(--color-primary)!"
                                        )}
                                    />
                                    <span className="font-medium">This and following sessions</span>
                                </label>

                                <label
                                    className={cn(
                                        "w-full rounded-md border p-3 flex items-center gap-3 text-left",
                                        deleteScope === "all" ? "border-(--color-primary)" : "border-(--neutral-300)",
                                        !allowBulkDelete && "opacity-60 cursor-not-allowed"
                                    )}
                                >
                                    <RadioGroupItem
                                        value="all"
                                        disabled={!allowBulkDelete}
                                        className={cn(
                                            "mt-0.5",
                                            "data-[state=checked]:border-(--color-primary) data-[state=checked]:text-(--color-primary)",
                                            "[&_[data-slot=radio-group-indicator]_svg]:!fill-(--color-primary)!"
                                        )}
                                    />
                                    <span className="font-medium">All sessions</span>
                                </label>
                            </RadioGroup>

                            {normalizedClassId === null ? (
                                <p className="text-sm text-(--neutral-500)">
                                    Bulk delete is disabled because this session payload did not include a class id.
                                </p>
                            ) : null}
                        </div>
                    </div>
                    <DialogFooter className="mt-4 flex gap-2 justify-between">
                        <DialogClose asChild>
                            <Button
                                variant="outline"
                                onClick={() => setOpen(false)}
                                disabled={loading}
                                className=""
                            >
                                Cancel
                            </Button>
                        </DialogClose>
                        <Button
                            variant=""
                            onClick={handleDelete}
                            disabled={loading}
                            className="bg-red text-white"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center w-full">
                                    <ClipLoader size={20} color="#fff" />
                                </span>
                            ) : (
                                "Delete"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}